import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Import Shared Components
import { Alert, CardGroup, Loader } from 'shared-components';
import { centsToDollars } from 'shared-components/src/utils/currency';
import ClientErrorComponent from '../../../components/ClientErrorComponent';
// Import Local Components
import CreateActions from './components/CreateActions';
import FieldDetails from './components/FieldDetails';
import UploadedValueUnloads from './components/UploadedValueUnloads';
// Import Component Styles
import { CreateHeader } from './styles';

// // Import Utils
import useTellerApi from '../../../hooks/useTellerApi';
import { post } from '../../../utils/http';
import { parse, parseValueLoadRows } from '../../../utils/csv';
import { mutationErrorMessageParser } from '../../../utils/errorLogger';

// Max number of allowable elements in file
import { VALUE_LOAD_FILE_UPLOAD_LENGTH_LIMIT } from '../../../utils/config';

function CreateValueUnloads() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loadStep, setLoadStep] = useState(1);
  const [valueUnloads, setValueUnloads] = useState([]);
  const [valueUnloadDetails, setValueUnloadDetails] = useState([]);
  const [upload, setUpload] = useState(null);

  // Get list of programs for company on mount
  const path = '/api/v1/client_portal/card_issuing/programs';
  const [
    {
      data: { data: programs },
      isLoading,
      isError,
    },
  ] = useTellerApi(path, {
    count: 0,
    data: [],
    limit: 100,
    offset: 0,
  });
  if (isLoading) return <Loader />;
  if (isError) return <ClientErrorComponent data={{}} />;


  // Function to calculate uploaded CSV value load details
  const getValueUnloadDetails = data => {
    const amount = centsToDollars(
      data.filter(x => x.amount).reduce((x, y) => x + y.amount, 0)
    );

    return {
      unloads: data.length,
      amount
    };
  };
  // Function to handle parsed and validated CSV data
  const handleUploadedLoadValues = data => {
    setValueUnloads(data);

    const details = getValueUnloadDetails(data);
    setValueUnloadDetails(details);
    setError(null);
    setSuccess('CSV has been validated successfully. Please review your submission and click Create Value Unload to begin processing.');
    setLoadStep(2);
    setLoading(false);
  };
  // Function to parse and validate uploaded CSV data
  const handleCSVParse = async e => {
    // Get file and clear file input
    const file = e.target.files[0];
    setUpload(file);
    e.target.value = null;

    try {
      setLoading(true);
      const rows = await parse(file);

      // Ensure that CSV file contains atleast one row of data
      if (rows.data.length <= 1) {
        throw new Error('Uploaded file does not contain any data');
      }

      if (rows.data.length > VALUE_LOAD_FILE_UPLOAD_LENGTH_LIMIT) {
        throw new Error(`Upload File contains more than ${VALUE_LOAD_FILE_UPLOAD_LENGTH_LIMIT} items, please remove some items from the file and try again`);
      }

      const data = rows.data
        .slice(1)
        .map((row, id) => parseValueLoadRows(row, id));

      handleUploadedLoadValues(data);
    } catch (err) {
      setLoading(false);
      setUpload(null);
      const message = mutationErrorMessageParser(err, 'Error uploading CSV.');
      setError(message);
    }
  };

  // Function to handle submission of Value Unloads to Teller
  const handleValueUnloadSubmit = async () => {
    try {
      setLoading(true);

      // Don't care about response since as long as the Value Unloads where accepted by Teller
      // the process is async from there on
      await post('/api/v1/client_portal/card_issuing/value_loads/bulk/unload', {value_unloads: valueUnloads});

      // Clear Value Unloads from State
      setValueUnloads([]);
      setLoading(false);
      // Send User to Value Unloads list page, with success message
      navigate('/card-issuing/value-loads', {
        state: { success: 'Value Unloads have been accepted and will begin processing. Value Unloads will appear in the list below as they are processed.' }
      });
    } catch (e) {
      setSuccess(null);
      setLoading(false);
      // Display Errror and keep Value Unloads in State to allow user to try again
      return setError('Error: Failed to Upload Value Unloads, please try again.');
    }
  };

  // If loading while csv is parsing or
  // Value Unloads are being uploaded display loader
  if (loading) return <Loader />;

  return (
    <>
      {error && <Alert type="danger">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <CardGroup>
        <CreateHeader>
          <CreateActions
            loadStep={loadStep}
            valueUnloadDetails={valueUnloadDetails}
          />
        </CreateHeader>
        {loadStep === 1 ? (
          <FieldDetails
            setLoadStep={setLoadStep}
            parseCsv={handleCSVParse}
          />
        ) : (
          <UploadedValueUnloads
            valueUnloads={valueUnloads}
            setValueUnloads={setValueUnloads}
            setLoadStep={setLoadStep}
            setSuccess={setSuccess}
            setError={setError}
            handleValueUnloadSubmit={handleValueUnloadSubmit}
          />
        )}
      </CardGroup>
    </>
  );
}

export default CreateValueUnloads;
